import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isStopTranscript } from '@utils/charting-voice-transcript.utils'

export type VoiceTranscriptStatus = 'pending' | 'applied' | 'info' | 'failed'

export interface VoiceTranscriptEntry {
  id: string
  text: string
  status: VoiceTranscriptStatus
  note?: string
  heard?: string
}

export interface VoiceTranscriptFinalizeOptions {
  status?: VoiceTranscriptStatus
  awaitResult?: boolean
  heard?: string
}

export interface VoiceTranscriptAnnotation {
  id?: string
  note?: string
  status: VoiceTranscriptStatus
  /** Only update an entry that has no outcome yet. */
  pendingOnly?: boolean
}

export interface VoiceTranscriptFeed {
  history: VoiceTranscriptEntry[]
  recognized: string
  liveText: string
  queued: string[]
  partial: (itemId: string, text: string) => void
  finalize: (
    itemId: string,
    text: string,
    options?: VoiceTranscriptFinalizeOptions,
  ) => void
  annotate: (update: VoiceTranscriptAnnotation) => string | undefined
  drop: (itemId: string) => void
  discard: () => void
  reset: () => void
}

const HISTORY_LIMIT = 5
const PARTIAL_THROTTLE_MS = 80

const comparableHeard = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[.,;:!?\u2019']/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')

/** Tracks speech per item so an older phrase never overwrites or clears a newer one. */
export function useVoiceTranscriptFeed(
  format: (text: string) => string = (text): string => text.trim(),
): VoiceTranscriptFeed {
  const [history, setHistory] = useState<VoiceTranscriptEntry[]>([])
  const [live, setLive] = useState<{ text: string; queued: string[] }>({
    text: '',
    queued: [],
  })
  const formatRef = useRef(format)
  formatRef.current = format
  const partials = useRef(new Map<string, string>())
  const finished = useRef(new Set<string>())
  const awaiting = useRef<string[]>([])
  const lastPublish = useRef(0)
  const timer = useRef<ReturnType<typeof setTimeout>>()

  const publish = useCallback((): void => {
    clearTimeout(timer.current)
    timer.current = undefined
    lastPublish.current = Date.now()
    const texts = Array.from(partials.current.values())
      .map((text) => formatRef.current(text))
      .filter(Boolean)
    const text = texts[texts.length - 1] ?? ''
    const queued = texts.slice(0, -1)
    setLive((current) =>
      current.text === text && current.queued.join('\n') === queued.join('\n')
        ? current
        : { text, queued },
    )
  }, [])

  useEffect(() => (): void => clearTimeout(timer.current), [])

  const partial = useCallback(
    (itemId: string, text: string): void => {
      if (finished.current.has(itemId)) return
      const isNew = !partials.current.has(itemId)
      partials.current.set(itemId, text)
      const elapsed = Date.now() - lastPublish.current
      if (isNew || elapsed >= PARTIAL_THROTTLE_MS) publish()
      else if (!timer.current) {
        timer.current = setTimeout(publish, PARTIAL_THROTTLE_MS - elapsed)
      }
    },
    [publish],
  )

  const finalize = useCallback(
    (
      itemId: string,
      text: string,
      options: VoiceTranscriptFinalizeOptions = {},
    ): void => {
      if (finished.current.has(itemId) && !partials.current.has(itemId)) return
      finished.current.add(itemId)
      partials.current.delete(itemId)
      publish()
      const trimmed = text.trim()
      const status = options.status ?? 'pending'
      const awaitResult = options.awaitResult ?? true
      const resolved = isStopTranscript(trimmed) ? 'applied' : status
      if (resolved === 'pending' && awaitResult) awaiting.current.push(itemId)
      const heard =
        options.heard &&
        comparableHeard(options.heard) !== comparableHeard(trimmed)
          ? options.heard.trim()
          : undefined
      setHistory((entries) =>
        [
          ...entries.filter((entry) => entry.id !== itemId),
          {
            id: itemId,
            text: trimmed,
            status: resolved,
            ...(heard ? { heard } : {}),
          },
        ].slice(-HISTORY_LIMIT),
      )
    },
    [publish],
  )

  const annotate = useCallback(
    ({
      id,
      note,
      status,
      pendingOnly = false,
    }: VoiceTranscriptAnnotation): string | undefined => {
      const target = id ?? awaiting.current[0]
      if (!target) return undefined
      awaiting.current = awaiting.current.filter((entry) => entry !== target)
      setHistory((entries) =>
        entries.map((entry) =>
          entry.id === target && (!pendingOnly || entry.status === 'pending')
            ? { ...entry, status, note: note ?? entry.note }
            : entry,
        ),
      )
      return target
    },
    [],
  )

  const drop = useCallback(
    (itemId: string): void => {
      finished.current.add(itemId)
      if (partials.current.delete(itemId)) publish()
    },
    [publish],
  )

  const discard = useCallback((): void => {
    partials.current.forEach((_, itemId) => finished.current.add(itemId))
    partials.current.clear()
    const dropped = new Set(awaiting.current)
    awaiting.current = []
    publish()
    if (dropped.size === 0) return
    setHistory((entries) =>
      entries.map((entry) =>
        dropped.has(entry.id)
          ? {
              ...entry,
              status: 'info',
              note: 'Discarded before it was applied.',
            }
          : entry,
      ),
    )
  }, [publish])

  const reset = useCallback((): void => {
    partials.current.clear()
    finished.current.clear()
    awaiting.current = []
    setHistory([])
    publish()
  }, [publish])

  const recognized = history[history.length - 1]?.text ?? ''

  return useMemo(
    () => ({
      history,
      recognized,
      liveText: live.text,
      queued: live.queued,
      partial,
      finalize,
      annotate,
      drop,
      discard,
      reset,
    }),
    [
      history,
      recognized,
      live,
      partial,
      finalize,
      annotate,
      drop,
      discard,
      reset,
    ],
  )
}
